import { computed, effect } from '@preact/signals'
import {
  Calendar,
  Clock,
  IdCard,
  LayoutDashboard,
  Plus,
  Users,
  X,
} from 'lucide-preact'
import { now } from '@01edu/time'

import { ErrorWithData } from '@01edu/api-client'
import { api, type ApiOutput } from '../lib/api.ts'
import { A, navigate, url } from '@01edu/signal-router'
import { isAdmin, onEvent } from '../lib/session.ts'
import { DialogModal } from '../ui/dialog.tsx'
import { useState } from 'preact/hooks'
import { formatDate } from '../lib/date-utils.ts'
import { groupScopes } from '../lib/scope-groups.ts'
import { ReferenceBadge } from '../ui/reference-badge.tsx'
import { ProjectOverviewSection } from './project-overview.tsx'
import {
  daysLeft,
  getLicenseStatus,
  type LicenseStatus,
  STATUS,
} from '../lib/status.ts'
import { Meter } from '../ui/meter.tsx'

type License = ApiOutput['GET/api/project/licenses']['licenses'][number]

const getLicenses = api['GET/api/project/licenses'].signal()
const createLicense = api['POST/api/project/license'].signal()

const applyFilters = (license: License) =>
  getLicenseStatus(license.licenseStartAt, license.licenseEndAt) ===
    url.params.status

const filteredLicenses = computed(() => {
  const licenses = getLicenses.data?.licenses || []
  return url.params.status ? licenses.filter(applyFilters) : licenses
})

const refreshLicensesAndScope = () => {
  const { project } = url.params
  project && getLicenses.fetch({ project })
}

onEvent(
  ['LICENSE_CREATED', 'LICENSE_UPDATED', 'LICENSE_DELETED'],
  refreshLicensesAndScope,
)
effect(refreshLicensesAndScope)

const errorReported = computed(() => {
  const { error } = createLicense
  if (!(error instanceof ErrorWithData)) return false
  const input = document.querySelector(
    `#create-license input[name=${error.data.input}]`,
  )
  if (!(input instanceof HTMLInputElement)) return false
  input.setCustomValidity(error.message)
  input.reportValidity()
  return true
})

const TimeElapsed = (
  { startAt, endAt }: { startAt: number; endAt: number },
) => {
  const status = getLicenseStatus(startAt, endAt)
  const elapsed = Math.min(Math.max(now() - startAt, 0), endAt - startAt)

  return (
    <Meter
      segments={[
        { value: elapsed, bg: STATUS[status].bg, label: 'Elapsed' },
        {
          value: endAt - startAt - elapsed,
          bg: 'bg-base-300',
          label: 'Remaining',
        },
      ]}
    />
  )
}

const LicenseCard = ({ license }: { license: License }) => {
  const { licenseStartAt: startAt, licenseEndAt: endAt } = license
  const status = getLicenseStatus(startAt, endAt)

  // h-full plus the bottom slot keep every card the same height and the time
  // bar on one line, including licenses that never expire and have no bar
  return (
    <A
      params={{ license: license.licenseId }}
      class='block'
    >
      <div
        class={`bg-base-100 border border-base-300 border-l-4 rounded-box shadow-sm hover:shadow-md overflow-hidden h-full ${
          STATUS[status].border
        }`}
      >
        <div class='p-6 h-full flex flex-col'>
          <div class='flex justify-between items-start mb-4'>
            <span class={`badge ${STATUS[status].badge}`}>
              {STATUS[status].label}
            </span>
          </div>

          <h3 class='text-lg font-bold text-base-content mb-6'>
            {license.licenseName || `License #${license.licenseId}`}
          </h3>

          <div class='space-y-3'>
            <div class='flex items-center gap-3'>
              <Users class='h-5 w-5 text-primary shrink-0' />
              <span class='text-sm text-base-content/80'>
                {license.licenseCapacity < 0
                  ? 'Unlimited seats'
                  : `${license.licenseCapacity} seats`}
              </span>
            </div>

            <div class='flex items-center gap-3'>
              <Calendar class='h-5 w-5 text-primary shrink-0' />
              <span class='text-sm text-base-content/80'>
                {formatDate(startAt)} to{' '}
                {endAt === -1 ? 'Never' : formatDate(endAt)}
              </span>
            </div>

            <div class='flex items-center gap-3'>
              <Clock class='h-5 w-5 text-primary shrink-0' />
              <span class='text-sm text-base-content/80'>
                {status === 'expired'
                  ? `Expired since ${formatDate(endAt)}`
                  : status === 'coming'
                  ? `Starts on ${formatDate(startAt)}`
                  : endAt === -1
                  ? 'Never expires'
                  : `${daysLeft(endAt)} days remaining`}
              </span>
            </div>
          </div>

          <div class='mt-auto pt-4 min-h-6 flex items-end'>
            {endAt !== -1 && <TimeElapsed startAt={startAt} endAt={endAt} />}
          </div>
        </div>
      </div>
    </A>
  )
}
export const LicenseSelect = (
  { defaultScopes }: { defaultScopes?: string[] },
) => {
  const remoteScopes = getLicenses.data?.remoteScopes || []
  const [selected, setSelected] = useState<string[]>(defaultScopes || [])

  const toggle = (val: string) => {
    setSelected((s) =>
      s.includes(val) ? s.filter((v) => v !== val) : [...s, val]
    )
  }

  const available = remoteScopes.filter((opt) => !selected.includes(opt.path))
  const groups = groupScopes(available)

  return (
    <div class='border border-base-300 bg-base-100'>
      <input
        type='hidden'
        name='licenseScopes'
        value={selected.join(',')}
        required
      />

      {selected.length > 0 && (
        <div class='px-4 py-3 border-b border-base-300 flex flex-wrap gap-2'>
          {selected.map((val) => (
            <div
              key={val}
              class='badge badge-primary badge-sm'
            >
              {val}
              <button
                type='button'
                onClick={() => toggle(val)}
                class='ml-1 hover:text-error transition-colors'
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div class='max-h-48 overflow-y-auto'>
        {groups.length > 0
          ? (
            groups.map((group) => (
              <details
                key={group.key}
                class='border-b border-base-300 last:border-b-0'
                open={groups.length === 1}
              >
                <summary class='px-4 py-2.5 text-sm font-semibold cursor-pointer hover:bg-base-200 flex items-center justify-between'>
                  <span class='flex items-center gap-2'>
                    <ReferenceBadge referencePath={group.referencePath} />
                    {group.name}
                    <span class='badge badge-ghost badge-sm'>
                      {group.scopes.length}
                    </span>
                  </span>
                  <button
                    type='button'
                    onClick={(e) => {
                      e.preventDefault()
                      setSelected((s) => [
                        ...s,
                        ...group.scopes.map((opt) => opt.path),
                      ])
                    }}
                    class='btn btn-ghost btn-xs text-primary'
                  >
                    Add all
                  </button>
                </summary>
                {group.scopes.map((opt) => (
                  <button
                    key={opt.path}
                    type='button'
                    title={opt.objectName ?? undefined}
                    onClick={() => toggle(opt.path)}
                    class='w-full text-left pl-8 pr-4 py-2 text-sm hover:bg-primary/10 hover:text-primary transition-colors'
                  >
                    {opt.path}
                  </button>
                ))}
              </details>
            ))
          )
          : (
            <div class='px-4 py-6 text-center text-base-content/50'>
              <p class='text-sm'>All scopes selected</p>
            </div>
          )}
      </div>

      {selected.length === 0 && (
        <div class='px-4 py-2 bg-error/10 border-t border-base-300'>
          <p class='text-xs text-error font-medium'>
            At least one scope is required
          </p>
        </div>
      )}
    </div>
  )
}

const handleUnlimitedToggle = (targetName: string) => (e: Event) => {
  const input = e.target as HTMLInputElement
  const form = input.form as HTMLFormElement
  if (!form) return
  const targetInput = form[targetName] as HTMLInputElement
  if (!targetInput) return
  targetInput.disabled = input.checked
  targetInput.required = !input.checked
  if (input.checked) targetInput.value = ''
}

export const handleUnlimitedInput = handleUnlimitedToggle('licenseCapacity')
export const handleUnlimitedDuration = handleUnlimitedToggle('licenseEndAt')

const handleSubmit = async (e: Event) => {
  e.preventDefault()
  const form = e.target as HTMLFormElement
  const licenseNameInput = form.licenseName as HTMLInputElement
  const licenseCapacityInput = form.licenseCapacity as HTMLInputElement
  const unlimitedInput = form.unlimited as HTMLInputElement
  const licenseStartAtInput = form.licenseStartAt as HTMLInputElement
  const licenseEndAtInput = form.licenseEndAt as HTMLInputElement
  const unlimitedDurationInput = form.unlimitedDuration as HTMLInputElement
  const licenseScopeInput = form.licenseScopes as HTMLInputElement

  const licenseScopes = licenseScopeInput.value
    ? licenseScopeInput.value.split(',')
    : []

  if (licenseScopes.length === 0) {
    licenseScopeInput.setCustomValidity('Please select at least one scope')
    licenseScopeInput.reportValidity()
    return
  }

  const licenseCapacity = unlimitedInput.checked
    ? -1
    : Number(licenseCapacityInput.value)

  await createLicense.fetch({
    projectId: Number(url.params.project) || 0,
    licenseName: licenseNameInput.value.trim() || undefined,
    licenseCapacity,
    licenseStartAt: Math.round(licenseStartAtInput.valueAsNumber / 1000),
    licenseEndAt: unlimitedDurationInput.checked
      ? -1
      : Math.round(licenseEndAtInput.valueAsNumber / 1000),
    licenseScopes,
  })

  if (createLicense.error) return
  form.reset()
  navigate({ params: { dialog: null }, replace: true })
}

const clearValidity = (e: Event) =>
  (e.target as HTMLInputElement).setCustomValidity('')

const CreateLicense = () => (
  <DialogModal id='create-license'>
    <h3 class='text-lg font-bold mb-4'>Create License</h3>
    <form onSubmit={handleSubmit} class='space-y-4'>
      <div class='space-y-2'>
        <span class='label-text text-sm'>License Name (optional)</span>
        <input
          name='licenseName'
          type='text'
          placeholder='e.g. Cohort 2025'
          class='input input-bordered input-sm w-full'
          maxLength={50}
        />
      </div>

      <div class='space-y-2'>
        <label class='flex items-center gap-2 cursor-pointer'>
          <input
            type='checkbox'
            name='unlimited'
            class='checkbox checkbox-sm checkbox-primary'
            onInput={handleUnlimitedInput}
          />
          <span class='label-text'>Unlimited Seats</span>
        </label>
        <input
          name='licenseCapacity'
          type='number'
          placeholder='Number of seats'
          class='input input-bordered input-sm w-full'
          onInput={clearValidity}
          required
          min={1}
        />
      </div>

      <div>
        <span class='label-text text-sm'>License Scopes</span>
        <LicenseSelect />
      </div>

      <div class='grid grid-cols-2 gap-3'>
        <div>
          <span class='label-text text-sm'>Start Date</span>
          <input
            name='licenseStartAt'
            type='date'
            class='input input-bordered input-sm w-full'
            onInput={clearValidity}
            required
          />
        </div>
        <div>
          <span class='label-text text-sm'>End Date</span>
          <input
            name='licenseEndAt'
            type='date'
            class='input input-bordered input-sm w-full'
            onInput={clearValidity}
            required
          />
        </div>
      </div>

      <label class='flex items-center gap-2 cursor-pointer'>
        <input
          type='checkbox'
          name='unlimitedDuration'
          class='checkbox checkbox-sm checkbox-primary'
          onInput={handleUnlimitedDuration}
        />
        <span class='label-text'>No expiration date</span>
      </label>

      {!errorReported.value && createLicense.error && (
        <p class='text-sm text-error'>{createLicense.error.message}</p>
      )}

      <button
        type='submit'
        class='btn btn-primary btn-sm w-full'
        disabled={!!createLicense.pending}
      >
        {createLicense.pending ? 'Creating...' : 'Create License'}
      </button>
    </form>
  </DialogModal>
)

const FilterBtn = ({ status }: { status: LicenseStatus }) => {
  const active = url.params.status === status
  return (
    <A
      class={[
        'btn btn-sm',
        STATUS[status].btn,
        active ? '' : 'btn-outline',
      ].join(' ')}
      params={{ status: active ? null : status }}
    >
      {STATUS[status].label}
    </A>
  )
}

const LicenseFilters = () => (
  <div class='flex flex-wrap items-center gap-2'>
    <FilterBtn status='active' />
    <FilterBtn status='coming' />
    <FilterBtn status='expired' />
    {url.params.status && (
      <A
        class='btn btn-ghost btn-sm btn-square'
        params={{ status: null }}
        replace
      >
        <X class='h-4 w-4' />
      </A>
    )}
  </div>
)

export const ProjectLicensesPage = () => {
  const currentView = url.params.view || 'overview'

  return (
    <div>
      <div class='container mx-auto pt-4 pb-6 px-4'>
        <div
          role='tablist'
          class='tabs tabs-box bg-base-100 border border-base-300 w-fit mb-6 p-1'
        >
          <A
            role='tab'
            params={{ view: 'overview', status: null }}
            class={`tab gap-2 ${
              currentView === 'overview' ? 'tab-active' : ''
            }`}
          >
            <LayoutDashboard class='h-4 w-4' />
            Overview
          </A>
          <A
            role='tab'
            params={{ view: 'licenses', 'scope-status': null }}
            class={`tab gap-2 ${
              currentView === 'licenses' ? 'tab-active' : ''
            }`}
          >
            <IdCard class='h-4 w-4' />
            Licenses
          </A>
        </div>

        {currentView === 'licenses' && (
          <div class='space-y-6'>
            <div class='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
              <LicenseFilters />
              {isAdmin.value && (
                <A
                  class='btn btn-primary btn-sm'
                  params={{ dialog: 'create-license' }}
                  replace
                >
                  <Plus class='h-4 w-4' />
                  Add License
                </A>
              )}
            </div>

            {filteredLicenses.value.length > 0
              ? (
                <div class='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                  {filteredLicenses.value.map((license) => (
                    <LicenseCard key={license.licenseId} license={license} />
                  ))}
                </div>
              )
              : (
                <div class='text-center py-12'>
                  <p class='text-base-content/60'>No licenses found</p>
                </div>
              )}
          </div>
        )}

        {currentView === 'overview' && <ProjectOverviewSection />}

        {isAdmin.value && <CreateLicense />}
      </div>
    </div>
  )
}
